import Vue from 'vue'

import {
    AUTH_ACCESS_TEAM_WITH_CALLBACK_URL,
    AUTH_AUTO_REQUEST,
    AUTH_FORCE_2FA_SETUP,
    AUTH_FORGOT,
    AUTH_LOGOUT,
    AUTH_ON_SUCCESS_RESPONSE,
    AUTH_REFRESH_DATA,
    AUTH_REGISTER,
    AUTH_REQUEST,
    AUTH_RESET,
    AUTH_SET_ACTIVE_TEAM,
    AUTH_SET_REDIRECT_PATH,
    AUTH_SET_STAFF_ACCESS_TOKEN,
    AUTH_SET_TEAM_MEMBERS,
    AUTH_SET_TOKEN,
    AUTH_SUCCESS,
    AUTH_USER_IS_STAFF_SET,
    AUTH_USER_PERMISSIONS_GET,
    AUTH_USER_PERMISSIONS_SET,
    AUTH_USER_TEAM_ROLES_SET,
    AVATARS_FETCH,
    AVATARS_SET,
    MEMBERS_PRESENCE_SET,
    ONBOARD_WELCOME_COMPLETE,
    ONBOARDING_REFRESH,
    SELECT_ACTIVE_TEAM_MODAL,
    SET_ONBOARD,
    SET_ONBOARD_STEPS,
    TEAM_CREATE,
    TEAM_UPDATE,
    TEAM_UPDATE_PROP,
    TEAMS_STATS_FETCH,
    TEAMS_STATS_SET,
    USER_FETCH_LIST,
    USER_UPDATE
} from '../../mutations'

import axios from 'axios'
import {INVITE_SET} from './invite'
import {Raven} from 'vue-raven'
import router from '../../../router'
import {ERROR, SUCCESS} from '../ui/UI'
import {getUrlSearchParameterByName} from '@/utils'
import {AppRoles} from '@/types/permissions/permissions'
import {Env} from '../../../../env'
import {checkIsActiveUser} from '@/types/data/user'

const initialState = () => ({
    authRedirectPath: null,
    token: null,

    permissions: { // user permissions from JWT
        hasDataEntry: false,
        hasTemplateEntry: false,
        hasAnalytics: false,
        hasTeamUser: false,
        hasImpersonate: false,
        hasManageStaff: false
    },
    /*
     * User roles for every user team from JWT.
     * {[teamUuid: String]: AppPermissions}
     */
    teamRoles: {},
    isStaff: false,
    user: { // @see User interface
        uuid: '',
        firstName: '',
        lastName: '',
        email: '',
        defaultTeam: null,
        forcePasswordChange: false,
        hasTwoFactor: false,
        isOnBoarded: true,
        isOnboardPurchasing: false,
        isOnboardSales: false,
        isOnboardStatements: false,
        isVerified: false,
        hash: null,
        createdAt: null
    },
    members: [],
    membersAvatars: {}, // [uuid]: String
    membersPresence: {}, // [uuid]: Boolean

    teams: [],
    teamsAvatars: {}, // [uuid]: String
    teamStats: [], // [{uuid: {String}, total: {Number}}]

    activeTeam: null,
    selectActiveTeamModal: {
        show: false,
        reason: null
    },
    onboarding: {}
})

const state = initialState()

/**
 * @param {Vuex.state} state
 * @param {AppRoles} roles
 * */
const _hasRole = (state, ...roles) => {
    if (!state.activeTeam || !state.teamRoles[state.activeTeam.uuid]) {
        return false
    }
    return roles.includes(state.teamRoles[state.activeTeam.uuid])
}

// GETTERS
const getters = {
    isDemoTeam: (state, getters) => getters.isZudelloDemoTeam || getters.isNexusSandboxTeam || getters.isCochraneTeam || getters.isNTAICTeam || getters.isCareFlightTeam || getters.isConnexaTeam,
    isZudelloDemoTeam: (state, getters) => getters.activeTeamId === 'c02c3339-7605-4dff-921a-56e62be1b0a3',
    isNexusSandboxTeam: (state, getters) => getters.activeTeamId === 'f905d4bb-b8c2-4937-af15-3716e99a1ec3' || getters.activeTeamId === 'b35cbf4c-04a4-4a7e-9379-3825fb451f7f' || getters.activeTeamId === 'c02c3339-7605-4dff-921a-56e62be1b0a3',
    isCochraneTeam: (state, getters) => getters.activeTeamId === '2d7d2871-1caf-4526-a7f6-86de6041251b' || getters.activeTeamId === 'f420add3-7f6a-479e-8f31-40e8cecc36d9',
    isConnexaTeam: (state, getters) => getters.activeTeamId === 'f85fcac2-7c04-406d-9e5e-43c339a6f896' || getters.activeTeamId === '38dbf2eb-8466-40ab-8228-e0e7869cfa75',
    isNTAICTeam: (state, getters) => getters.activeTeamId === '00465923-0ff1-44e2-a637-e30754ec85d8' || getters.activeTeamId === 'f420add3-7f6a-479e-8f31-40e8cecc36d9',
    isMedhealthTeam: (state, getters) => getters.activeTeamId === '9ed6e60b-26cf-4a54-a4d4-3a9e7d3128f1' || getters.activeTeamId === 'fabf9fca-b796-45a6-a176-5bf73558dc56',
    isCareFlightTeam: (state, getters) => getters.activeTeamId === '982ebda8-f94e-46a0-8213-4dfc431eb157' || getters.activeTeamId === '1933e765-b18d-4618-aebc-31a4d079cac1',
    isProcurementDemoTeam: (state, getters) => getters.activeTeamId === 'e7bbe651-fd56-4910-a666-41612697122a' || getters.activeTeamId === 'f905d4bb-b8c2-4937-af15-3716e99a1ec3' || getters.activeTeamId === 'b35cbf4c-04a4-4a7e-9379-3825fb451f7f' || getters.activeTeamId === 'c02c3339-7605-4dff-921a-56e62be1b0a3' || getters.activeTeamId === 'f85fcac2-7c04-406d-9e5e-43c339a6f896' || getters.activeTeamId === '38dbf2eb-8466-40ab-8228-e0e7869cfa75',
    isExactContracting: (state, getters) => getters.activeTeamId === 'adafdd0a-a121-4796-8b91-571b0abb5109',
    showDashboardReports: (state, getters) => getters.isExactContracting,

    getToken: state => state.token,
    authRedirectPath: state => state.authRedirectPath,

    // USER
    isAuthenticated: state => !!state.token,
    fullName: state => state.user ? `${state.user.firstName} ${state.user.lastName}` : 'User',
    userUUID: state => (state.user.uuid).trim(),
    userLocations: (state, getters) => _.isArray(getters.getCurrentMember.locations) ? getters.getCurrentMember.locations : [],
    userSubsidiaries: (state, getters) => _.isArray(getters.getCurrentMember.subsidiaries) ? getters.getCurrentMember.subsidiaries : [],
    userDefaultSettings: (state, getters) => getters.getCurrentMember.default || {},
    userDefaultLocation: (state, getters) => getters.userDefaultSettings.location,
    userDefaultSubsidiary: (state, getters) => getters.userDefaultSettings.subsidiary,
    checkUserHasLocationAccess: (state, getters) => locationId => {
        if (getters.userLocations.length === 0) {
            return getters.getLocations.some(l => l.uuid === locationId)
        }
        return getters.userLocations.some(l => l.uuid === locationId)
    },
    checkUserHasSubsidiaryAccess: (state, getters) => subsidiaryId => {
        if (getters.userSubsidiaries.length === 0) {
            return getters.getSubsidiaries.some(s => s.uuid === subsidiaryId)
        }
        return getters.userSubsidiaries.some(s => s.uuid === subsidiaryId)
    },
    getTeams: state => state.teams || [],
    getTeamById: (state, getters) => teamId => getters.getTeams.find(t => t.uuid === teamId),
    getUser: state => state.user,
    getMembers: state => _.uniqBy(state.members, 'uuid') || [], // TODO: remove duplicates from api request
    getActiveMembers: (state, getters) => getters.getMembers.filter(m => checkIsActiveUser(m)),
    getMemberByUuid: (state, getters) => uuid => getters.getMembers.find(m => m.uuid === uuid),
    getCurrentMember: (state, getters) => getters.getMemberByUuid(getters.userUUID) || {}, // NOTE: differs with getUser (contains locations, delegate...)
    isAccountVerified: state => state.user.isVerified,
    isOnBoarded: state => state.user.isOnBoarded,

    // TEAM
    teamRoles: state => state.teamRoles,
    teamStats: state => state.teamStats,
    activeTeam: (state, getters) => _.get(state, 'activeTeam', getters.getTeams[0]),
    activeTeamId: (state, getters) => _.get(getters.activeTeam, 'uuid'),
    activeTeamTimezone: (state, getters) => _.get(getters.activeTeam, 'timezone', 'Australia/Brisbane'),
    activeTeamCurrencyCode: (state, getters) => _.get(getters.activeTeam, 'currency.code', 'USD'),
    selectActiveTeamModal: state => state.selectActiveTeamModal,
    priceAlerts: (state, getters) => getters.activeTeam.priceAlerts || {},

    // onboarding (TODO: onboarding module, some steps like integration are deprecated)
    getOnboarding: state => state.onboarding || {},
    onboardingSteps: (state, getters) => getters.getOnboarding.steps || [],
    hasAllOnboardingStepsPassed: (state, getters) => getters.onboardingSteps.filter(s => s.complete).length === getters.onboardingSteps.length,
    hasFirstUploadedInvoice: (state, getters) => getters.onboardingSteps.some(s => s.uuid === '1' && !!s.complete),
    hasInvitedMember: (state, getters) => getters.onboardingSteps.some(s => s.uuid === '2' && !!s.complete),
    hasAddedIntegration: (state, getters) => getters.onboardingSteps.some(s => s.uuid === '3' && !!s.complete),
    hasApprovedInvoice: (state, getters) => getters.onboardingSteps.some(s => s.uuid === '4' && !!s.complete),

    permissions: state => state.permissions,
    // User role for active team
    isStaff: state => state.isStaff,
    isAdmin: state => _hasRole(state, AppRoles.ADMIN),
    isMember: state => _hasRole(state, AppRoles.MEMBER),
    isAdminOrStaff: state => _hasRole(state, AppRoles.ADMIN, AppRoles.STAFF),

    membersPresence: state => state.membersPresence || {},
    membersAvatars: state => state.membersAvatars || {},
    teamsAvatars: state => state.teamsAvatars || {},

    isDataEntryTeam: (state, getters, rootState) => _.get(rootState, 'route.params.team') === Env.dataEntryTeamId,
    hasDataEntryAvailable: (state, getters) => getters.isDataEntryTeam && getters.permissions.hasDataEntry
}

const getTeamFromAuthResponseData = ({res, teamId = ''}) => {
    const user = _.get(res, 'data.user')
    const teams = _.get(res, 'data.teams', [])
    let team = teams.find(t => t.uuid === teamId)
    if (!team) {
        team = teams.find(t => t.uuid === _.get(user, 'defaultTeam'))
    }
    if (!team) {
        team = _.head(teams)
    }
    return team
}

export const actions = {
    [AUTH_ON_SUCCESS_RESPONSE]({commit, dispatch}, {res, teamId = ''}) {
        commit(AUTH_SET_TOKEN, _.get(res, 'data.token'))
        commit(AUTH_SUCCESS, res)
        commit(AUTH_SET_ACTIVE_TEAM, getTeamFromAuthResponseData({res, teamId}))
        commit(INVITE_SET, null)
    },

    [AUTH_REQUEST]: ({commit, dispatch, getters}, user) => {
        return new Promise((resolve, reject) => {
            axios.post('login', user, {requestId: AUTH_REQUEST})
                .then(res => {
                    dispatch(AUTH_ON_SUCCESS_RESPONSE, {res})
                    resolve()
                })
                .catch(err => {
                    commit(AUTH_SET_TOKEN, null)
                    reject(err)
                })
        })
    },

    [AUTH_AUTO_REQUEST]: ({commit, dispatch, getters}, user) => {
        return new Promise((resolve, reject) => {
            axios.post(`login/auto/${user.uuid}?expires=${user.expires}&signature=${user.sig}`, user, {requestId: AUTH_AUTO_REQUEST})
                .then(res => {
                    dispatch(AUTH_ON_SUCCESS_RESPONSE, {res})
                    resolve()
                })
                .catch(err => {
                    commit(AUTH_SET_TOKEN, null)
                    reject(err)
                })
        })
    },

    [AUTH_ACCESS_TEAM_WITH_CALLBACK_URL]: ({commit, dispatch, getters}, accessUrl) => {
        return new Promise((resolve, reject) => {
            axios.post(accessUrl, {requestId: AUTH_ACCESS_TEAM_WITH_CALLBACK_URL})
                .then(res => {
                    dispatch(AUTH_ON_SUCCESS_RESPONSE, {
                        res,
                        teamId: getUrlSearchParameterByName(accessUrl, 'team')
                    })
                    Vue.prototype.$snackbar.show({type: SUCCESS, text: 'Access granted'})
                    resolve()
                })
                .catch(e => {
                    Vue.prototype.$snackbar.show({type: ERROR})
                    commit(AUTH_SET_TOKEN, null)
                    reject(e)
                })
        })
    },

    [AUTH_LOGOUT]: ({commit}) => {
        return axios.post('logout').finally(() => {
            commit(AUTH_LOGOUT)
        })
    },

    [AUTH_REGISTER]: ({commit, dispatch, getters}, user) => {
        return new Promise((resolve, reject) => {
            axios.post('register', user)
                .then(res => {
                    dispatch(AUTH_ON_SUCCESS_RESPONSE, {res})
                    resolve()
                })
                .catch(err => {
                    commit(AUTH_SET_TOKEN, null)
                    reject(err)
                })
        })
    },

    [AUTH_FORGOT]: ({commit, dispatch}, user) => {
        return new Promise((resolve, reject) => {
            axios.post('password/email', user)
                .then(res => {
                    resolve(res)
                })
                .catch(err => {
                    reject(err)
                })
        })
    },

    [AUTH_RESET]: ({commit, dispatch}, user) => {
        return new Promise((resolve, reject) => {
            axios.post(`password/reset?expires=${user.expires}&uuid=${user.uuid}&signature=${user.sig}`, {
                password: user.password
            })
                .then(res => {
                    resolve(res)
                })
                .catch(err => {
                    reject(err)
                })
        })
    },

    [AUTH_SET_STAFF_ACCESS_TOKEN]: ({commit, dispatch, getters, rootState}, teamId) => {
        if (!getters.isStaff) {
            return
        }
        teamId = teamId || rootState.route.params.team
        return axios.post(`staff/auth/${teamId}/access`, null, {requestId: AUTH_SET_STAFF_ACCESS_TOKEN})
            .then(res => {
                dispatch(AUTH_ON_SUCCESS_RESPONSE, {res, teamId})
                return res.data.token
            })
            .catch(e => {
                Raven.captureException(e)
                return null
            })
    },

    [ONBOARD_WELCOME_COMPLETE]: async({commit}) => {
        await axios.post('users/onboard/WEBSITE', null, {requestId: ONBOARD_WELCOME_COMPLETE})
        commit(SET_ONBOARD)
    },

    [ONBOARDING_REFRESH]: async({commit}) => {
        const res = await axios.get('users/onboarding', {requestId: ONBOARDING_REFRESH})
        commit(SET_ONBOARD_STEPS, res.data)
    },

    [TEAM_CREATE]: ({commit, dispatch}, team) => {
        return new Promise((resolve, reject) => {
            axios.post('teams/simple', team)
                .then(async res => {
                    commit(AUTH_SET_TOKEN, res.data.token)
                    commit(AUTH_SUCCESS, res)
                    const newTeam = res.data.teams.find(team => team.uuid === res.data.newTeam)
                    await Vue.prototype.$snackbar.show({
                        type: 'SUCCESS',
                        text: `Team "${newTeam.name}" successfully created`
                    })
                    resolve(newTeam)
                })
                .catch(err => {
                    commit(AUTH_SET_TOKEN, null)
                    reject(err)
                })
        })
    },

    [AUTH_REFRESH_DATA]: async({commit, dispatch}) => {
        try {
            const res = await axios.post('auth/refresh')
            commit(AUTH_REFRESH_DATA, res)
            if (!res || !res.data || !res.data.teams || res.data.teams.length === 0) { // the user can be removed from all teams via the staff portal, but still authenticated and with an active team
                commit(AUTH_SET_ACTIVE_TEAM, null)
                return router.push({name: 'login'}).catch(() => {
                })
            }
            await dispatch(USER_FETCH_LIST)
            await dispatch(AVATARS_FETCH)
        } catch (e) {
            if (!axios.isCancel(e)) {
                Raven.captureException(e)
            }
        }
    },

    [USER_FETCH_LIST]: async({commit}) => {
        const res = await axios.get('teams/users', {requestId: USER_FETCH_LIST})
        commit(AUTH_SET_TEAM_MEMBERS, res.data.data || [])
    },

    [TEAMS_STATS_FETCH]: async({commit}) => {
        const res = await axios.get('teams/stats', {requestId: TEAMS_STATS_FETCH})
        commit(TEAMS_STATS_SET, res.data || [])
    },

    [AUTH_USER_PERMISSIONS_GET]: ({commit, getters}, payload) => {
        const token = getters.getToken || payload
        if (!token) {
            return
        }
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const decodedJWT = JSON.parse(window.atob(base64))

        if (decodedJWT) {
            const permissionsJWT = decodedJWT.PERMISSIONS ? decodedJWT.PERMISSIONS : null
            if (permissionsJWT) {
                const permissions = getters.permissions
                permissionsJWT.forEach(el => {
                    if (el.permission) {
                        permissions[el.permission] = true
                    }
                })
                commit(AUTH_USER_PERMISSIONS_SET, permissions)
            }

            const teamRoles = decodedJWT.TEAM_ROLES ? decodedJWT.TEAM_ROLES : null
            if (teamRoles) {
                for (const role in teamRoles) {
                    if (teamRoles.hasOwnProperty(role) && teamRoles[role]) {
                        teamRoles[role] = teamRoles[role].toUpperCase()
                    }
                }
                commit(AUTH_USER_TEAM_ROLES_SET, teamRoles)
            }

            commit(AUTH_USER_IS_STAFF_SET, !!decodedJWT.IS_STAFF)
        }
    },

    [AVATARS_FETCH]: ({commit, getters}) => {
        return axios.post('avatars', {
            users: getters.getMembers.map(m => m.uuid),
            teams: getters.getTeams.map(t => t.uuid)
        })
            .then(res => {
                commit(AVATARS_SET, res.data)
            })
            .catch(e => {
                Raven.captureException(e)
            })
    },

    [AUTH_FORCE_2FA_SETUP]: () => {
        return axios.post('users/2fa')
            .then(res => {
                const qrCodeImg = res.data && res.data.qrCode ? res.data.qrCode : ''
                Vue.prototype.$modal.show('setup-two-factor', {qrCodeImg, force: true})
                return 200 // additional return info to hold continue action (like team select on integrate)
            })
            .catch(e => {
                if (e.response.status === 400) { // already with two factor
                    return 400 // Force 2fa: already enabled'
                }
                Raven.captureException(e)
                return 500 // error
            })
    }
}

export const mutations = {
    [AUTH_SET_REDIRECT_PATH]: (state, path) => {
        state.authRedirectPath = path
    },
    [AUTH_SET_TOKEN]: (state, token) => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
        } else {
            delete axios.defaults.headers.common['Authorization']
            delete axios.defaults.headers.common['x-team']
            delete axios.defaults.headers.common['x-platform']
        }
        state.token = token
    },
    [AUTH_USER_PERMISSIONS_SET]: (state, payload) => {
        state.permissions = payload
    },
    [AUTH_USER_TEAM_ROLES_SET]: (state, payload) => {
        state.teamRoles = payload
    },
    [AUTH_USER_IS_STAFF_SET]: (state, payload) => {
        state.isStaff = payload
    },
    [SELECT_ACTIVE_TEAM_MODAL]: (state, data) => {
        state.selectActiveTeamModal = data
    },
    [AUTH_SET_ACTIVE_TEAM]: (state, team) => {
        if (team && team.uuid) {
            axios.defaults.headers.common['x-team'] = team.uuid
        }
        state.activeTeam = {...team}
    },
    [AUTH_SET_TEAM_MEMBERS]: (state, members) => {
        state.members = members || []
    },
    [AUTH_SET_TEAM_MEMBERS]: (state, members) => {
        state.members = members || []
    },
    [AUTH_SUCCESS]: (state, data) => {
        if (!data || !data.data) {
            return
        }
        state.user = data.data.user
        state.teams = (data.data.teams && data.data.teams.length > 0) ? data.data.teams : []
        state.token = data.data.token
    },
    [AUTH_LOGOUT]: (state) => {
        const s = initialState()
        Object.keys(s).forEach(key => {
            state[key] = s[key]
        })
    },
    [USER_UPDATE]: (state, data) => {
        state.user = {...state.user, ...data}
    },
    [TEAM_UPDATE]: (state, data) => {
        state.activeTeam = {...state.activeTeam, ...data}
    },
    [TEAM_UPDATE_PROP]: (state, {prop, data}) => {
        state.activeTeam[prop] = {...state.activeTeam[prop], ...data}
    },
    [AUTH_REFRESH_DATA]: (state, data) => {
        if (data && data.data) {
            state.user = data.data.user
            state.teams = data.data.teams || []
        }
    },
    [AVATARS_SET]: (state, {teams, users}) => {
        state.membersAvatars = users || {}
        state.teamsAvatars = teams || {}
    },
    [MEMBERS_PRESENCE_SET]: (state, members) => {
        state.membersPresence = members || {}
    },
    [SET_ONBOARD]: state => {
        state.user.isOnBoarded = true
    },
    [SET_ONBOARD_STEPS]: (state, rsp) => {
        state.onboarding = rsp
    },
    [TEAMS_STATS_SET]: (state, data) => {
        state.teamStats = data || []
    }
}

export default {
    state,
    getters,
    actions,
    mutations
}
